// PublicShop.jsx — Nova Skin Care storefront (/shop). Dark editorial luxury to
// match the Nova Glow landing: deep green-charcoal, cream + gold, Fraunces serif,
// products spotlit on light "podiums" (photo white-bg removed via multiply).
// Cart persists in localStorage; view/add/checkout/purchase logged to shop_events.
// Checkout + payment server-verified via the shop-checkout edge function.
import { useState, useEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { supabase } from "./supabase";

const stripePromise = loadStripe("pk_live_51TPSNN9gc0MT8Jg7aFesfPT0xlWbQ5fx1kHIpHqw1nwna52Z0sai0h5L0nHbQfO9kRII1On3Ag4ikZwO6zCJ9vuc00FIGFrxoh");

const C = {
  bg:"#20261f", panel:"#2c352b", panel2:"#323c30", line:"rgba(241,236,225,0.12)",
  cream:"#f1ece1", text:"#ece7da", muted:"#9aa893", stone:"#c9c1b0",
  gold:"#c8a86a", goldD:"#9c7e34", goldL:"#e6cf95", goldText:"#241c0c",
  green:"#7fc99b", red:"#e0a18f",
};
const F = "'Manrope','Montserrat',system-ui,sans-serif";
const S = "'Fraunces','Playfair Display','Cormorant Garamond',Georgia,serif";
const money = (n) => "$" + Number(n || 0).toFixed(2);
const HI = (u) => !u ? u : (u.includes("cdn.shopify.com") ? u + (u.includes("?") ? "&" : "?") + "width=1400" : u);

// Product spotlit on a glowing light podium — multiply removes the photo's white bg.
function Podium({ p, pct = 76 }) {
  const url = p?.image_url ? HI(p.image_url) : null;
  return (
    <div style={{position:"relative",width:"100%",aspectRatio:"1/1",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <span style={{position:"absolute",inset:"9%",borderRadius:"50%",background:"radial-gradient(circle at 50% 38%,#fcf8ef,#efe6d3 60%,#e3d8c1 100%)",boxShadow:"inset 0 2px 26px rgba(255,255,255,.5),0 24px 50px rgba(0,0,0,.35)"}}/>
      {url
        ? <img src={url} alt={p.name} loading="lazy" decoding="async" style={{position:"relative",zIndex:2,maxWidth:pct+"%",maxHeight:(pct+4)+"%",objectFit:"contain",mixBlendMode:"multiply"}}/>
        : <span style={{position:"relative",zIndex:2,fontSize:40,opacity:0.4}}>🧴</span>}
    </div>
  );
}

// ── anon session id (conversion funnel) ──
function sessionId() {
  let id = localStorage.getItem("nova_shop_sid");
  if (!id) { id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("nova_shop_sid", id); }
  return id;
}
async function track(event_type, extra = {}) {
  try { await supabase.from("shop_events").insert({ session_id: sessionId(), event_type, user_agent: navigator.userAgent.slice(0, 200), ...extra }); }
  catch (e) { /* non-blocking */ }
}
const loadCart = () => { try { return JSON.parse(localStorage.getItem("nova_shop_cart") || "[]"); } catch { return []; } };
const saveCart = (c) => localStorage.setItem("nova_shop_cart", JSON.stringify(c));

export default function PublicShop() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(loadCart());
  const [cartOpen, setCartOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const [cat, setCat] = useState("All");
  const viewedRef = useRef(false);

  useEffect(() => { load(); }, []);
  useEffect(() => { saveCart(cart); }, [cart]);

  const load = async () => {
    const [{ data: prods }, { data: st }] = await Promise.all([
      supabase.from("store_products").select("*").eq("active", true).order("featured", { ascending: false }).order("order_index").order("name"),
      supabase.from("store_settings").select("*").eq("id", 1).single(),
    ]);
    setProducts(prods || []);
    setSettings(st || {});
    setLoading(false);
    if (!viewedRef.current) { viewedRef.current = true; track("view_shop"); }
  };

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartSubtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const addToCart = (p, qty = 1) => {
    setCart(prev => {
      const ex = prev.find(i => i.product_id === p.id);
      return ex
        ? prev.map(i => i.product_id === p.id ? { ...i, qty: Math.min(99, i.qty + qty) } : i)
        : [...prev, { product_id: p.id, name: p.name, price: Number(p.price), image_url: p.image_url, qty }];
    });
    track("add_to_cart", { product_id: p.id, product_name: p.name, qty, value: Number(p.price) * qty });
    setCartOpen(true);
  };
  const setQty = (pid, qty) => setCart(prev => qty <= 0 ? prev.filter(i => i.product_id !== pid) : prev.map(i => i.product_id === pid ? { ...i, qty } : i));
  const removeItem = (pid) => { const it = cart.find(i => i.product_id === pid); if (it) track("remove_from_cart", { product_id: pid, product_name: it.name }); setCart(prev => prev.filter(i => i.product_id !== pid)); };

  const cats = ["All", ...Array.from(new Set(products.map(p => p.category).filter(Boolean)))];
  const shown = cat === "All" ? products : products.filter(p => p.category === cat);

  if (loading) return <div style={{minHeight:"100vh",background:C.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F,color:C.muted}}>Loading…</div>;

  if (checkout) return <Checkout cart={cart} settings={settings} onBack={() => setCheckout(false)} onDone={() => { setCart([]); saveCart([]); }} />;

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:F,color:C.text}}>
      {/* Top bar */}
      <div style={{position:"sticky",top:0,zIndex:50,background:"rgba(32,38,31,0.78)",backdropFilter:"blur(14px)",borderBottom:"1px solid "+C.line,padding:"15px 22px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <a href="/" style={{fontFamily:S,fontSize:21,fontWeight:500,color:C.cream,letterSpacing:"0.04em",textDecoration:"none"}}>NOVA <span style={{color:C.gold}}>Skin Care</span></a>
        <button onClick={()=>setCartOpen(true)} style={{position:"relative",background:"none",border:"1px solid "+C.line,borderRadius:99,padding:"8px 18px",cursor:"pointer",fontFamily:F,fontSize:13,fontWeight:700,color:C.cream}}>
          Cart{cartCount>0 && <span style={{marginLeft:7,background:C.gold,color:C.goldText,borderRadius:99,padding:"1px 8px",fontSize:11,fontWeight:800}}>{cartCount}</span>}
        </button>
      </div>

      {/* Hero */}
      <div style={{position:"relative",padding:"58px 24px 38px",textAlign:"center",maxWidth:820,margin:"0 auto",overflow:"hidden"}}>
        <div style={{position:"absolute",left:"50%",top:"10%",transform:"translateX(-50%)",width:520,height:520,borderRadius:"50%",background:"radial-gradient(circle,rgba(200,168,106,.16),transparent 64%)",filter:"blur(20px)",pointerEvents:"none"}}/>
        <div style={{position:"relative",fontSize:12,letterSpacing:"0.3em",color:C.gold,fontWeight:700,marginBottom:14,textTransform:"uppercase"}}>Nova Med Spa</div>
        <h1 style={{position:"relative",fontFamily:S,fontSize:"clamp(34px,6vw,56px)",fontWeight:400,color:C.cream,lineHeight:1.04,margin:"0 0 16px"}}>{settings?.hero_heading || "Nova Skin Care"}</h1>
        <p style={{position:"relative",fontSize:16,color:C.stone,lineHeight:1.6,maxWidth:540,margin:"0 auto"}}>{settings?.hero_subheading || "Medical-grade skincare, curated by our providers."}</p>
      </div>

      {/* Category filter */}
      {cats.length > 2 && (
        <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap",padding:"0 20px 30px",maxWidth:1100,margin:"0 auto"}}>
          {cats.map(c => (
            <button key={c} onClick={()=>setCat(c)} style={{padding:"8px 17px",borderRadius:99,border:"1px solid "+(cat===c?C.gold:C.line),background:cat===c?C.gold:"transparent",color:cat===c?C.goldText:C.stone,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:F}}>{c}</button>
          ))}
        </div>
      )}

      {/* Grid */}
      <div style={{maxWidth:1140,margin:"0 auto",padding:"0 18px 90px",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(244px,1fr))",gap:20}}>
        {shown.length === 0 && <div style={{gridColumn:"1/-1",textAlign:"center",color:C.muted,padding:60}}>No products yet — check back soon.</div>}
        {shown.map(p => (
          <ProductCard key={p.id} p={p} onView={()=>{ setDetail(p); track("view_product",{product_id:p.id,product_name:p.name,value:Number(p.price)}); }} onAdd={()=>addToCart(p)} />
        ))}
      </div>

      <Footer settings={settings} />

      {detail && <ProductModal p={detail} onClose={()=>setDetail(null)} onAdd={(q)=>{addToCart(detail,q);setDetail(null);}} />}
      {cartOpen && <CartDrawer cart={cart} subtotal={cartSubtotal} settings={settings} onClose={()=>setCartOpen(false)} onQty={setQty} onRemove={removeItem} onCheckout={()=>{setCartOpen(false);setCheckout(true);}} />}
    </div>
  );
}

function ProductCard({ p, onView, onAdd }) {
  const onSale = p.compare_at_price && Number(p.compare_at_price) > Number(p.price);
  const out = p.track_stock && (p.stock_qty ?? 0) <= 0;
  return (
    <div style={{background:"linear-gradient(180deg,"+C.panel+","+C.panel2+")",border:"1px solid "+C.line,borderRadius:20,overflow:"hidden",display:"flex",flexDirection:"column",transition:"transform .3s,border-color .3s",cursor:"pointer"}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-6px)";e.currentTarget.style.borderColor="rgba(200,168,106,.4)";}}
      onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.borderColor=C.line;}}>
      <div onClick={onView} style={{position:"relative",padding:"14px 14px 0"}}>
        <Podium p={p} />
        {onSale && <span style={{position:"absolute",top:18,left:18,background:C.gold,color:C.goldText,fontSize:10,fontWeight:800,padding:"3px 9px",borderRadius:99,letterSpacing:"0.05em",zIndex:3}}>SALE</span>}
        {out && <span style={{position:"absolute",top:18,right:18,background:"rgba(0,0,0,.5)",color:C.cream,fontSize:10,fontWeight:700,padding:"3px 9px",borderRadius:99,zIndex:3}}>SOLD OUT</span>}
      </div>
      <div style={{padding:"6px 16px 17px",display:"flex",flexDirection:"column",flex:1}}>
        {p.brand && <div style={{fontSize:10,letterSpacing:"0.14em",color:C.muted,fontWeight:700,textTransform:"uppercase",marginBottom:4}}>{p.brand}</div>}
        <div onClick={onView} style={{fontFamily:S,fontSize:17,fontWeight:500,color:C.cream,lineHeight:1.22,marginBottom:4}}>{p.name}</div>
        {p.short_description && <div style={{fontSize:12.5,color:C.stone,lineHeight:1.45,marginBottom:10,flex:1}}>{p.short_description}</div>}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:"auto",gap:8}}>
          <div style={{display:"flex",alignItems:"baseline",gap:7}}>
            <span style={{fontSize:17,fontWeight:800,color:C.cream}}>{money(p.price)}</span>
            {onSale && <span style={{fontSize:12,color:C.muted,textDecoration:"line-through"}}>{money(p.compare_at_price)}</span>}
          </div>
          <button disabled={out} onClick={onAdd} style={{padding:"9px 16px",borderRadius:99,border:out?"1px solid "+C.line:"none",background:out?"transparent":"linear-gradient(135deg,"+C.goldL+","+C.gold+")",color:out?C.muted:C.goldText,fontWeight:800,fontSize:12.5,cursor:out?"not-allowed":"pointer",fontFamily:F}}>{out?"Sold out":"Add"}</button>
        </div>
      </div>
    </div>
  );
}

function ProductModal({ p, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const onSale = p.compare_at_price && Number(p.compare_at_price) > Number(p.price);
  const out = p.track_stock && (p.stock_qty ?? 0) <= 0;
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(12,14,11,0.7)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:18,backdropFilter:"blur(6px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.panel,borderRadius:22,maxWidth:780,width:"100%",maxHeight:"92vh",overflow:"auto",display:"grid",gridTemplateColumns:"1fr 1fr",border:"1px solid "+C.line}}>
        <div style={{background:"radial-gradient(120% 90% at 50% 30%,#2f382f,#20261f)",minHeight:320,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{width:"100%",maxWidth:300}}><Podium p={p} pct={82}/></div>
        </div>
        <div style={{padding:"28px 28px 26px"}}>
          {p.brand && <div style={{fontSize:11,letterSpacing:"0.16em",color:C.gold,fontWeight:700,textTransform:"uppercase",marginBottom:6}}>{p.brand}</div>}
          <h2 style={{fontFamily:S,fontSize:27,fontWeight:500,color:C.cream,margin:"0 0 10px",lineHeight:1.12}}>{p.name}</h2>
          <div style={{display:"flex",alignItems:"baseline",gap:9,marginBottom:14}}>
            <span style={{fontSize:23,fontWeight:800,color:C.cream}}>{money(p.price)}</span>
            {onSale && <span style={{fontSize:15,color:C.muted,textDecoration:"line-through"}}>{money(p.compare_at_price)}</span>}
          </div>
          {p.description && <p style={{fontSize:14,color:C.stone,lineHeight:1.6,marginBottom:18,whiteSpace:"pre-wrap"}}>{p.description}</p>}
          {!out && (
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <div style={{display:"flex",alignItems:"center",border:"1px solid "+C.line,borderRadius:99}}>
                <button onClick={()=>setQty(q=>Math.max(1,q-1))} style={{padding:"8px 15px",background:"none",border:"none",cursor:"pointer",fontSize:16,color:C.cream}}>−</button>
                <span style={{padding:"0 8px",fontWeight:700,minWidth:24,textAlign:"center",color:C.cream}}>{qty}</span>
                <button onClick={()=>setQty(q=>Math.min(99,q+1))} style={{padding:"8px 15px",background:"none",border:"none",cursor:"pointer",fontSize:16,color:C.cream}}>+</button>
              </div>
            </div>
          )}
          <button disabled={out} onClick={()=>onAdd(qty)} style={{width:"100%",padding:"15px",borderRadius:99,border:"none",background:out?C.panel2:"linear-gradient(135deg,"+C.goldL+","+C.gold+")",color:out?C.muted:C.goldText,fontWeight:800,fontSize:15,cursor:out?"not-allowed":"pointer",fontFamily:F}}>{out?"Sold out":`Add to cart · ${money(p.price*qty)}`}</button>
          <button onClick={onClose} style={{width:"100%",marginTop:10,padding:"10px",background:"none",border:"none",color:C.muted,cursor:"pointer",fontFamily:F,fontSize:13}}>Continue shopping</button>
        </div>
      </div>
    </div>
  );
}

function CartDrawer({ cart, subtotal, settings, onClose, onQty, onRemove, onCheckout }) {
  const freeThreshold = settings?.free_ship_threshold;
  const toFree = freeThreshold != null ? Math.max(0, Number(freeThreshold) - subtotal) : null;
  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(12,14,11,0.6)",zIndex:200,display:"flex",justifyContent:"flex-end",backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.bg,width:"100%",maxWidth:430,height:"100vh",display:"flex",flexDirection:"column",borderLeft:"1px solid "+C.line}}>
        <div style={{padding:"18px 22px",borderBottom:"1px solid "+C.line,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:S,fontSize:21,fontWeight:500,color:C.cream}}>Your Cart</div>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:26,color:C.muted,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{flex:1,overflow:"auto",padding:"16px 22px"}}>
          {cart.length === 0 && <div style={{textAlign:"center",color:C.muted,padding:50}}>Your cart is empty.</div>}
          {cart.map(it => (
            <div key={it.product_id} style={{display:"flex",gap:13,paddingBottom:16,marginBottom:16,borderBottom:"1px solid "+C.line}}>
              <div style={{width:66,height:66,borderRadius:12,flexShrink:0,overflow:"hidden",background:"radial-gradient(circle at 50% 38%,#fcf8ef,#e3d8c1)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                {it.image_url ? <img src={HI(it.image_url)} alt="" style={{width:"82%",height:"82%",objectFit:"contain",mixBlendMode:"multiply"}}/> : <span style={{opacity:0.4}}>🧴</span>}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:700,color:C.cream,marginBottom:2}}>{it.name}</div>
                <div style={{fontSize:13,color:C.gold,fontWeight:700,marginBottom:6}}>{money(it.price)}</div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{display:"flex",alignItems:"center",border:"1px solid "+C.line,borderRadius:8}}>
                    <button onClick={()=>onQty(it.product_id,it.qty-1)} style={{padding:"4px 11px",background:"none",border:"none",cursor:"pointer",fontSize:14,color:C.cream}}>−</button>
                    <span style={{padding:"0 6px",fontSize:13,fontWeight:700,color:C.cream}}>{it.qty}</span>
                    <button onClick={()=>onQty(it.product_id,it.qty+1)} style={{padding:"4px 11px",background:"none",border:"none",cursor:"pointer",fontSize:14,color:C.cream}}>+</button>
                  </div>
                  <button onClick={()=>onRemove(it.product_id)} style={{background:"none",border:"none",color:C.muted,fontSize:12,cursor:"pointer",textDecoration:"underline"}}>Remove</button>
                </div>
              </div>
              <div style={{fontWeight:800,color:C.cream,fontSize:14}}>{money(it.price*it.qty)}</div>
            </div>
          ))}
        </div>
        {cart.length > 0 && (
          <div style={{padding:"18px 22px",borderTop:"1px solid "+C.line,background:C.panel}}>
            {toFree != null && toFree > 0 && <div style={{fontSize:12,color:C.gold,marginBottom:10,textAlign:"center"}}>Add {money(toFree)} more for free shipping ✦</div>}
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
              <span style={{color:C.muted}}>Subtotal</span><span style={{fontWeight:800,fontSize:18,color:C.cream}}>{money(subtotal)}</span>
            </div>
            <button onClick={onCheckout} style={{width:"100%",padding:"15px",borderRadius:99,border:"none",background:"linear-gradient(135deg,"+C.goldL+","+C.gold+")",color:C.goldText,fontWeight:800,fontSize:15,cursor:"pointer",fontFamily:F}}>Checkout</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Checkout ──
function Checkout({ cart, settings, onBack, onDone }) {
  const [info, setInfo] = useState({ name:"", email:"", phone:"" });
  const [fulfillment, setFulfillment] = useState(settings?.pickup_enabled === false ? "ship" : "pickup");
  const [addr, setAddr] = useState({ line1:"", line2:"", city:"", state:"", zip:"" });
  const [stage, setStage] = useState("info");
  const [clientSecret, setClientSecret] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const subtotal = cart.reduce((s,i)=>s+i.price*i.qty,0);
  const shipEnabled = settings?.ship_enabled !== false;
  const pickupEnabled = settings?.pickup_enabled !== false;
  const freeThreshold = settings?.free_ship_threshold;
  const shippingFee = fulfillment === "ship" ? ((freeThreshold!=null && subtotal>=Number(freeThreshold)) ? 0 : Number(settings?.shipping_fee||0)) : 0;
  const tax = Math.round(subtotal * Number(settings?.tax_rate||0) * 100)/100;
  const total = Math.round((subtotal + shippingFee + tax)*100)/100;

  const validInfo = info.name.trim() && /\S+@\S+\.\S+/.test(info.email) && info.phone.replace(/\D/g,"").length>=10 &&
    (fulfillment==="pickup" || (addr.line1.trim() && addr.city.trim() && addr.state.trim() && addr.zip.trim()));

  const startPayment = async () => {
    setErr(""); setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("shop-checkout", {
        body: { action:"create_order", cart: cart.map(i=>({product_id:i.product_id,qty:i.qty})), fulfillment_type: fulfillment, shipping_address: fulfillment==="ship" ? addr : null, customer: info, session_id: sessionId() },
      });
      if (error || data?.error) throw new Error(data?.error || error.message);
      setClientSecret(data.client_secret); setOrderInfo({ order_id:data.order_id, order_number:data.order_number, totals:data.totals });
      setStage("pay");
    } catch (e) { setErr(e.message || "Could not start checkout"); }
    finally { setBusy(false); }
  };

  if (stage === "done") {
    return (
      <div style={{minHeight:"100vh",background:C.bg,fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
        <div style={{textAlign:"center",maxWidth:460}}>
          <div style={{fontSize:50,marginBottom:14,color:C.gold}}>✦</div>
          <h1 style={{fontFamily:S,fontSize:32,fontWeight:500,color:C.cream,margin:"0 0 12px"}}>Thank you.</h1>
          <p style={{color:C.stone,fontSize:15,lineHeight:1.6}}>Your order <b style={{color:C.gold}}>{orderInfo?.order_number}</b> is confirmed. {fulfillment==="ship" ? "We'll email you tracking once it ships." : "We'll text you when it's ready for pickup at the spa."} A receipt is on its way to {info.email}.</p>
          <a href="/shop" style={{display:"inline-block",marginTop:24,padding:"14px 30px",background:"linear-gradient(135deg,"+C.goldL+","+C.gold+")",color:C.goldText,borderRadius:99,textDecoration:"none",fontWeight:800,fontFamily:F}}>Continue shopping</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:C.bg,fontFamily:F,color:C.text}}>
      <div style={{maxWidth:580,margin:"0 auto",padding:"26px 18px 70px"}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,cursor:"pointer",fontSize:13,marginBottom:14}}>← Back to shop</button>
        <h1 style={{fontFamily:S,fontSize:32,fontWeight:500,color:C.cream,margin:"0 0 22px"}}>Checkout</h1>

        {/* Order summary */}
        <div style={{background:C.panel,border:"1px solid "+C.line,borderRadius:18,padding:"18px 20px",marginBottom:18}}>
          {cart.map(it=>(
            <div key={it.product_id} style={{display:"flex",justifyContent:"space-between",fontSize:14,marginBottom:8}}>
              <span style={{color:C.text}}>{it.name} <span style={{color:C.muted}}>× {it.qty}</span></span>
              <span style={{fontWeight:700,color:C.cream}}>{money(it.price*it.qty)}</span>
            </div>
          ))}
          <div style={{borderTop:"1px solid "+C.line,marginTop:10,paddingTop:12,fontSize:14,color:C.muted}}>
            <Row l="Subtotal" v={money(subtotal)} />
            {fulfillment==="ship" && <Row l="Shipping" v={shippingFee===0?"FREE":money(shippingFee)} />}
            {tax>0 && <Row l="Tax" v={money(tax)} />}
            <div style={{display:"flex",justifyContent:"space-between",marginTop:8,fontSize:19,fontWeight:800,color:C.cream}}><span>Total</span><span>{money(total)}</span></div>
          </div>
        </div>

        {stage === "info" && (
          <>
            {(shipEnabled && pickupEnabled) && (
              <div style={{display:"flex",gap:10,marginBottom:18}}>
                {[["pickup","Pickup at spa"],["ship","Ship to me"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setFulfillment(v)} style={{flex:1,padding:"14px",borderRadius:13,border:"1.5px solid "+(fulfillment===v?C.gold:C.line),background:fulfillment===v?"rgba(200,168,106,.12)":"transparent",color:C.cream,fontWeight:700,fontSize:13.5,cursor:"pointer",fontFamily:F}}>{l}</button>
                ))}
              </div>
            )}
            {fulfillment==="pickup" && <div style={{fontSize:13,color:C.stone,marginBottom:16,padding:"12px 15px",background:C.panel,border:"1px solid "+C.line,borderRadius:12}}>📍 Pickup at {settings?.pickup_address || "Nova Med Spa"}. We'll text you when it's ready.</div>}

            <Field label="Full name" value={info.name} onChange={v=>setInfo({...info,name:v})} />
            <Field label="Email" value={info.email} onChange={v=>setInfo({...info,email:v})} type="email" />
            <Field label="Phone" value={info.phone} onChange={v=>setInfo({...info,phone:v})} type="tel" />
            {fulfillment==="ship" && (
              <>
                <Field label="Address" value={addr.line1} onChange={v=>setAddr({...addr,line1:v})} />
                <Field label="Apt / Suite (optional)" value={addr.line2} onChange={v=>setAddr({...addr,line2:v})} />
                <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10}}>
                  <Field label="City" value={addr.city} onChange={v=>setAddr({...addr,city:v})} />
                  <Field label="State" value={addr.state} onChange={v=>setAddr({...addr,state:v})} />
                  <Field label="ZIP" value={addr.zip} onChange={v=>setAddr({...addr,zip:v})} />
                </div>
              </>
            )}
            {err && <div style={{color:C.red,fontSize:13,marginBottom:12}}>{err}</div>}
            <button disabled={!validInfo||busy} onClick={startPayment} style={{width:"100%",padding:"16px",borderRadius:99,border:"none",background:validInfo&&!busy?"linear-gradient(135deg,"+C.goldL+","+C.gold+")":C.panel2,color:validInfo&&!busy?C.goldText:C.muted,fontWeight:800,fontSize:15,cursor:validInfo&&!busy?"pointer":"not-allowed",fontFamily:F}}>{busy?"Preparing…":`Continue to payment · ${money(total)}`}</button>
          </>
        )}

        {stage === "pay" && clientSecret && (
          <Elements stripe={stripePromise} options={{clientSecret,locale:"en",appearance:{theme:"night",variables:{colorPrimary:C.gold,colorBackground:C.panel,colorText:C.cream,colorTextSecondary:C.muted,fontFamily:"Manrope, sans-serif",borderRadius:"10px"}}}}>
            <PayForm orderInfo={orderInfo} total={total} onSuccess={()=>{ track("purchase",{value:total,order_id:orderInfo?.order_id,customer_email:info.email}); onDone(); setStage("done"); }} onErr={setErr} />
            {err && <div style={{color:C.red,fontSize:13,marginTop:10}}>{err}</div>}
            <button onClick={()=>{setStage("info");setClientSecret(null);}} style={{width:"100%",marginTop:10,padding:"10px",background:"none",border:"none",color:C.muted,cursor:"pointer",fontFamily:F,fontSize:13}}>← Edit details</button>
          </Elements>
        )}
      </div>
    </div>
  );
}

function PayForm({ orderInfo, total, onSuccess, onErr }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true); onErr("");
    const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (error) { onErr(error.message || "Payment failed"); setBusy(false); return; }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      try { await supabase.functions.invoke("shop-checkout", { body: { action:"confirm_order", order_id: orderInfo.order_id, payment_intent_id: paymentIntent.id } }); }
      catch (e) { /* paid in Stripe; confirm is best-effort */ }
      onSuccess();
    } else { onErr("Payment could not be completed."); setBusy(false); }
  };
  return (
    <div>
      <PaymentElement />
      <button disabled={!stripe||busy} onClick={submit} style={{width:"100%",marginTop:16,padding:"16px",borderRadius:99,border:"none",background:busy?C.panel2:"linear-gradient(135deg,"+C.goldL+","+C.gold+")",color:busy?C.muted:C.goldText,fontWeight:800,fontSize:15,cursor:busy?"not-allowed":"pointer",fontFamily:F}}>{busy?"Processing…":`Pay ${money(total)}`}</button>
      <div style={{textAlign:"center",fontSize:11,color:C.muted,marginTop:10}}>🔒 Secure payment by Stripe</div>
    </div>
  );
}

function Footer({ settings }) {
  return (
    <div style={{borderTop:"1px solid "+C.line,padding:"34px 20px",textAlign:"center",color:C.muted,fontSize:12,lineHeight:1.7}}>
      <div style={{fontFamily:S,fontSize:17,color:C.cream,marginBottom:6}}>Nova Med Spa</div>
      {settings?.pickup_address || "1451 Old Country Rd, Plainview NY 11803"}<br/>
      <a href="https://www.novamedicalspa.com" style={{color:C.gold,textDecoration:"none"}}>novamedicalspa.com</a>
    </div>
  );
}

function Row({ l, v }) { return <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}><span>{l}</span><span>{v}</span></div>; }
function Field({ label, value, onChange, type="text" }) {
  return (
    <div style={{marginBottom:12}}>
      <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{label}</label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} style={{width:"100%",padding:"13px 15px",border:"1px solid "+C.line,borderRadius:11,fontSize:15,fontFamily:F,color:C.cream,outline:"none",boxSizing:"border-box",background:C.panel}}/>
    </div>
  );
}
